import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { StockComparisonSnapshot } from "../../../types/StockComparison";
import MetricRow, { FormatType } from "./ComparisonMetricRow";
import { useCompanyProfile } from "../Charts/hooks/useCompanyProfile";
import ComparisonTechnicalPanel from "./ComparisonTechnicalPanel";

interface MetricDef {
  label: string;
  key: keyof StockComparisonSnapshot;
  format: FormatType;
  higherIsBetter?: boolean;
  negativeIsValid?: boolean; // when true, negative values are meaningful and comparable (e.g. net debt)
}

const GROWTH_METRICS: MetricDef[] = [
  { label: "Revenue Growth (YoY)", key: "revenueGrowthYoY", format: "percent", higherIsBetter: true },
  { label: "Revenue Growth (3Y CAGR)", key: "revenueGrowth3Y", format: "percent", higherIsBetter: true },
  { label: "Revenue Growth (5Y CAGR)", key: "revenueGrowth5Y", format: "percent", higherIsBetter: true },
  { label: "EPS Growth (YoY)", key: "epsGrowthYoY", format: "percent", higherIsBetter: true },
  { label: "EPS Growth (3Y CAGR)", key: "epsGrowth3Y", format: "percent", higherIsBetter: true },
  { label: "EPS Growth (5Y CAGR)", key: "epsGrowth5Y", format: "percent", higherIsBetter: true },
  { label: "FCF Growth (YoY)", key: "fcfGrowthYoY", format: "percent", higherIsBetter: true },
  { label: "SBC % of Revenue", key: "sbcToRevenue", format: "percent", higherIsBetter: false },
];

const VALUATION_METRICS: MetricDef[] = [
  { label: "P/E (TTM)", key: "peRatioTTM", format: "ratio", higherIsBetter: false },
  { label: "Forward P/E (Next FY)", key: "forwardPE", format: "ratio", higherIsBetter: false },
  { label: "Forward P/E (+2 FY)", key: "forwardPE2Y", format: "ratio", higherIsBetter: false },
  { label: "Price / Book", key: "priceToBookTTM", format: "ratio", higherIsBetter: false },
  { label: "Price / Sales", key: "priceToSalesTTM", format: "ratio", higherIsBetter: false },
  { label: "Price / FCF", key: "priceToFCFTTM", format: "ratio", higherIsBetter: false },
  { label: "EV / EBITDA", key: "evToEBITDATTM", format: "ratio", higherIsBetter: false },
];

const PROFITABILITY_METRICS: MetricDef[] = [
  { label: "Gross Margin", key: "grossMargin", format: "percent", higherIsBetter: true },
  { label: "Operating Margin", key: "operatingMargin", format: "percent", higherIsBetter: true },
  { label: "Net Margin", key: "netMargin", format: "percent", higherIsBetter: true },
  { label: "FCF Margin", key: "fcfMarginTTM", format: "percent", higherIsBetter: true },
  { label: "ROE (TTM)", key: "roeTTM", format: "percent", higherIsBetter: true },
  { label: "ROIC (TTM)", key: "roicTTM", format: "percent", higherIsBetter: true },
];

const HEALTH_METRICS: MetricDef[] = [
  { label: "Cash", key: "cash", format: "currency", higherIsBetter: true },
  { label: "Total Debt", key: "totalDebt", format: "currency", higherIsBetter: false },
  { label: "Net Debt", key: "netDebt", format: "currency", higherIsBetter: false, negativeIsValid: true },
  { label: "Debt / Equity", key: "debtToEquityTTM", format: "ratio", higherIsBetter: false },
  { label: "Interest Coverage", key: "interestCoverageTTM", format: "ratio", higherIsBetter: true },
];


const determineWinner = (
  v1: number | null,
  v2: number | null,
  higherIsBetter?: boolean,
  negativeIsValid?: boolean,
): boolean => {
  if (higherIsBetter === undefined || v1 === null || v2 === null || v1 === v2) return false;
  // For lower-is-better metrics where negatives are meaningless (e.g. valuation
  // ratios), a negative value signals an unprofitable company and should never
  // beat a positive value. Skip this guard for metrics like net debt where a
  // negative value is genuinely meaningful (net cash position).
  if (!higherIsBetter && !negativeIsValid) {
    if (v1 < 0 && v2 >= 0) return false;
    if (v1 >= 0 && v2 < 0) return true;
    if (v1 < 0 && v2 < 0) return false;
  }
  return higherIsBetter ? v1 > v2 : v1 < v2;
};

interface StockCardProps {
  sectionTitle: string;
  snapshot: StockComparisonSnapshot | null;
  otherSnapshot: StockComparisonSnapshot | null;
  metrics: MetricDef[];
  color: string;
}

const StockCard = ({ sectionTitle, snapshot, otherSnapshot, metrics, color }: StockCardProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const { data: profileData } = useCompanyProfile(snapshot?.ticker ?? "");
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;
  const currency = profile?.currency;

  const border = theme.palette.divider;
  const textMuted = isDark ? "#6b7280" : "#94a3b8";
  const textPrimary = theme.palette.text.primary;
  // Header uses the page background (darkest layer) to sit above surface rows
  const headerBg = theme.palette.background.default;

  if (snapshot === null) {
    return (
      <Box
        sx={{
          flex: 1,
          border: `1px solid ${border}`,
          borderRadius: 1,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
          p: 4,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box
          sx={{
            border: `1px dashed ${border}`,
            borderRadius: 1,
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.875rem", color: textMuted }}>
            Add a stock to compare
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        border: `1px solid ${border}`,
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: headerBg,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: textPrimary,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {sectionTitle}
        </Typography>
      </Box>

      {/* Metric rows */}
      <Box>
        {metrics.map((def) => {
          const thisValue = snapshot[def.key] as number | null;
          const otherValue = (otherSnapshot?.[def.key] ?? null) as number | null;
          const isWinner = otherSnapshot !== null && determineWinner(thisValue, otherValue, def.higherIsBetter, def.negativeIsValid);

          return (
            <MetricRow
              key={String(def.key)}
              label={def.label}
              value={thisValue}
              format={def.format}
              currency={currency}
              isWinner={isWinner}
              winnerColor={color}
            />
          );
        })}
      </Box>
    </Box>
  );
};

interface SectionBlockProps {
  title: string;
  metrics: MetricDef[];
  snapshot1: StockComparisonSnapshot | null;
  snapshot2: StockComparisonSnapshot | null;
  color1: string;
  color2: string;
}

const SectionBlock = ({ title, metrics, snapshot1, snapshot2, color1, color2 }: SectionBlockProps) => (
  <Box sx={{ display: "flex", gap: { xs: 2, sm: 6 } }}>
    <StockCard
      sectionTitle={title}
      snapshot={snapshot1}
      otherSnapshot={snapshot2}
      metrics={metrics}
      color={color1}
    />
    <StockCard
      sectionTitle={title}
      snapshot={snapshot2}
      otherSnapshot={snapshot1}
      metrics={metrics}
      color={color2}
    />
  </Box>
);

interface ComparisonMetricsTableProps {
  snapshot1: StockComparisonSnapshot | null;
  snapshot2: StockComparisonSnapshot | null;
}

const ComparisonMetricsTable = ({ snapshot1, snapshot2 }: ComparisonMetricsTableProps) => {
  const theme = useTheme();
  const color = theme.palette.primary.main;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 2.5 } }}>
      <SectionBlock title="Growth" metrics={GROWTH_METRICS} snapshot1={snapshot1} snapshot2={snapshot2} color1={color} color2={color} />
      <SectionBlock title="Valuation" metrics={VALUATION_METRICS} snapshot1={snapshot1} snapshot2={snapshot2} color1={color} color2={color} />
      <SectionBlock title="Profitability" metrics={PROFITABILITY_METRICS} snapshot1={snapshot1} snapshot2={snapshot2} color1={color} color2={color} />
      <SectionBlock title="Financial Health" metrics={HEALTH_METRICS} snapshot1={snapshot1} snapshot2={snapshot2} color1={color} color2={color} />
      <Box sx={{ display: "flex", gap: { xs: 2, sm: 6 } }}>
        <ComparisonTechnicalPanel snapshot={snapshot1} color={color} />
        <ComparisonTechnicalPanel snapshot={snapshot2} color={color} />
      </Box>
    </Box>
  );
};

export default ComparisonMetricsTable;
